import React, { useState } from 'react';
import { Box, Button, IconButton } from '@ellucian/react-design-system/core';
import { Tooltip, CircularProgress } from '@mui/material';
import { Icon } from '@ellucian/ds-icons/lib';
import { updateCachedSelfEntitlementStatus } from '../utils/selfEntitlementService';
import SelfEntitleConfirmModal from './SelfEntitleConfirmModal';

interface SelfEntitleButtonProps {
  solutionId: string;
  fullHierarchy: string;  // From solution metadata (Product Full_Hierarchy__c)
  name: string;  // Short solution name
  erpType: string;
  selfEntitledIds?: string[];  // Optional: for checking if already entitled
  remainingCount?: number;  // Optional: from parent (HomePage/SolutionHeader) for limit enforcement
  onError?: (message: string) => void;
  onSuccess?: () => void;
}

const SelfEntitleButton: React.FC<SelfEntitleButtonProps> = ({
  solutionId,
  fullHierarchy,
  name,
  erpType,
  onSuccess,
  remainingCount,
  onError,
}) => {
  // React hooks must be called before any early returns
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);

  // Safety check: Don't render if required fields are missing
  if (!solutionId || !fullHierarchy || !name || !erpType) {
    return null;
  }

  // Determine if button should be disabled (limit reached)
  const isDisabled = remainingCount !== undefined && remainingCount <= 0;
  const limitMessage = isDisabled ? `Limit reached for ${erpType} (30 maximum)` : null;

  const handleSelectIntegration = () => {
    // Don't allow selection if limit reached
    if (isDisabled) {
      return;
    }
    setError(null); // Clear any previous error
    setShowConfirmModal(true);
  };

  const handleConfirmSelection = async () => {
    // Keep modal open, start loading
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${baseUrl}/api/self-entitle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'cookie-based-auth'
        },
        credentials: 'include', // Include cookies for JWT auth (contains accountId in appSession)
        body: JSON.stringify({
          solutionId,
          fullHierarchy,  // From solution metadata (Product Full_Hierarchy__c)
          name,  // Short solution name
          erpType,
          // accountId is extracted from JWT by backend; not needed in body
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        let errorMessage = 'Failed to select integration';
        
        if (response.status === 403) {
          errorMessage = 'Limit exceeded for this ERP type';
        } else if (response.status === 409) {
          // Already entitled or duplicate attempt during TMS sync
          errorMessage = 'Already entitled to this solution. Please contact support if this persists.';
        } else {
          errorMessage = 'Failed to select integration';
        }
        
        setError(errorMessage);
        // Close the confirmation modal on error so the user returns to the page-level alert
        setShowConfirmModal(false);
        if (onError) {
          onError(errorMessage);
        }
        setLoading(false);
        return;
      }

      // POST succeeded - use response data directly (no need for extra GET /status call)
      const postResponse = await response.json();
      console.log('[SelfEntitle] POST success:', postResponse);

      // Update entitlements cache (add newly entitled solution)
      try {
        const entitlementsJson = localStorage.getItem('entitlementsData');
        if (entitlementsJson) {
          const entitlements = JSON.parse(entitlementsJson);
          
          // Check if this solution is already in entitlements
          const alreadyExists = entitlements.some(
            (e: any) => e.salesforceId === solutionId
          );
          
          // If not already there, add it
          if (!alreadyExists) {
            entitlements.push({
              salesforceId: solutionId,
              name: fullHierarchy,
            });
            localStorage.setItem('entitlementsData', JSON.stringify(entitlements));
            console.log('[SelfEntitle] Updated entitlementsData cache with new solution');
          }
        }
      } catch (cacheErr) {
        console.warn('[SelfEntitle] Failed to update entitlementsData cache:', cacheErr);
        // Non-critical - continue even if this fails
      }

      setSuccess(true);
      setLoading(false);
      
      // Close modal on success
      setShowConfirmModal(false);

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      setError('Network error. Please try again.');
      // Close the confirmation modal on network error as well
      setShowConfirmModal(false);
      if (onError) {
        onError('Network error. Please try again.');
      }
      setLoading(false);
    }
  };

  const tooltipText = remainingCount !== undefined && remainingCount <= 0 
    ? `Limit reached for ${erpType} (30 maximum integrations)`
    : 'Selecting this integration allows you to install it later. Each selection counts toward your core integration limit.';

  const isLimitReached = remainingCount !== undefined && remainingCount <= 0;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '6px', md: '12px' }, flexWrap: 'nowrap' }}>
        <Button
          onClick={handleSelectIntegration}
          disabled={loading || success || isDisabled}
          color="primary"
          variant="contained"
          disableElevation
          sx={{
            ml: { xs: 0, md: 2 },
            fontFamily: 'Poppins, Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
            color: 'var(--Neutral-neutral-100, #FFF)',
            fontSize: { xs: '12px', md: '16px' },
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '110%',
            textTransform: 'capitalize',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            '&:disabled': {
              color: 'var(--Neutral-neutral-400, #9CA3AF)',
              backgroundColor: 'var(--Neutral-neutral-200, #E5E7EB)',
            },
          }}
          title={limitMessage || 'Select this integration'}
        >
          {loading && <CircularProgress size={16} aria-label="Loading" sx={{ color: '#ffffff' }} />}
          {isDisabled ? 'Limit Reached' : success ? 'Selected ✓' : 'Select Integration'}
        </Button>

        <Tooltip
          title={tooltipText}
          open={showInfoTooltip}
          onOpen={() => setShowInfoTooltip(true)}
          onClose={() => setShowInfoTooltip(false)}
          enterDelay={0}
          leaveDelay={100}
          arrow
          placement="bottom"
          disableInteractive={false}
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: '#ffffff',
                color: '#151618',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                padding: { xs: '10px 12px', md: '16px 20px' },
                maxWidth: { xs: '160px', sm: '180px', md: '250px' },
                fontFamily: 'Noto Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',
                fontWeight: 400,
                fontSize: { xs: '11px', sm: '12px', md: '14px' },
                lineHeight: { xs: '14px', sm: '16px', md: '21px' },
                letterSpacing: '0%',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                wordBreak: 'break-word',
              },
            },
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 10],
                  },
                },
              ],
            },
          }}
        >
          <IconButton
            color="gray"
            aria-label="Information"
            size="small"
            onClick={() => setShowInfoTooltip(!showInfoTooltip)}
            sx={{
              padding: { xs: '6px', md: '8px' },
              minWidth: { xs: '28px', md: '32px' },
              minHeight: { xs: '28px', md: '32px' },
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <Icon name="info" style={{ width: '16px', height: '16px' }} />
          </IconButton>
        </Tooltip>
      </Box>

      <SelfEntitleConfirmModal
        open={showConfirmModal}
        solutionName={fullHierarchy}
        onConfirm={handleConfirmSelection}
        onCancel={() => {
          setError(null); // Clear error when canceling
          setShowConfirmModal(false);
        }}
        isSubmitting={loading}
      />
    </>
  );
};

export default SelfEntitleButton;
